import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import Loader from '../components/Loader';

interface Chat {
  id: string;
  otherUser: {
    telegramId: number;
    firstName: string;
  };
  lastMessage?: {
    text: string;
    createdAt: string;
    senderId: number;
  };
  unreadCount: number;
}

export default function Chats() {
  const { user } = useAuthStore();
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const response = await apiService.getChats();
      setChats(response.data.data || []);
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Вчера';
    } else if (days < 7) {
      return date.toLocaleDateString('ru-RU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
  };

  if (isLoading) {
    return <Loader className="py-12" />;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">💬 Сообщения</h1>

      {chats.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-4xl mb-2">💬</p>
          <p className="text-tg-theme-hint">У вас пока нет сообщений</p>
          <p className="text-sm text-tg-theme-hint mt-1">
            Начните общение, написав продавцу
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {chats.map((chat) => (
            <Link key={chat.id} to={`/chats/${chat.id}`}>
              <div className="card flex items-center gap-3 hover:opacity-90">
                <div className="w-12 h-12 rounded-full bg-tg-theme-button flex items-center justify-center text-white text-xl flex-shrink-0">
                  {chat.otherUser.firstName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold truncate">{chat.otherUser.firstName}</h3>
                    {chat.lastMessage && (
                      <span className="text-xs text-tg-theme-hint">
                        {formatTime(chat.lastMessage.createdAt)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-tg-theme-hint truncate">
                      {chat.lastMessage?.senderId === user?.telegramId && (
                        <span className="text-tg-theme-button">Вы: </span>
                      )}
                      {chat.lastMessage?.text || 'Нет сообщений'}
                    </p>
                    {chat.unreadCount > 0 && (
                      <span className="bg-tg-theme-button text-white text-xs rounded-full px-2 py-0.5 ml-2">
                        {chat.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
